import type { WithdrawalRequest } from '../types';

type WithdrawalTypeContext = {
  withdrawalType?: string | null;
  provider?: string | null;
  metadata?: Record<string, unknown> | null;
};

/** CDN / game credit withdrawals — not fiat USD payout rails */
export function isCreditWithdrawal(w: WithdrawalTypeContext): boolean {
  if (w.withdrawalType === 'game_withdrawal' || w.withdrawalType === 'hierarchy_withdrawal') {
    return true;
  }
  const provider = String(w.provider || '').toLowerCase();
  if (
    provider === 'admin_cash' ||
    provider === 'shop_player_cash' ||
    provider === 'agent_cash' ||
    provider === 'super_agent_cash'
  ) {
    return true;
  }
  const meta = w.metadata ?? {};
  if (meta.adminCashWithdrawal === true || meta.adminCashFunding === true) return true;
  if (meta.agentWithdrewCash === true || meta.superAgentWithdrewCash === true) return true;
  if (meta.fundingSource === 'shop_counter') return true;
  return false;
}

const TYPE_LABELS: Record<string, string> = {
  bank_transfer: 'Bank Transfer',
  bitcoin_transfer: 'Bitcoin',
  litecoin_transfer: 'Litecoin',
  dogecoin_transfer: 'Dogecoin',
  pyusd_transfer: 'PYUSD',
  ecash_transfer: 'eCash',
  cashapp_transfer: 'Cash App',
  chime_transfer: 'Chime',
  game_withdrawal: 'CREDIT',
  hierarchy_withdrawal: 'Hierarchy credit',
};

/** Rails shown in withdrawal / flagged-withdrawal type filters (includes GGUS Cash App + Chime). */
export const WITHDRAWAL_RAIL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'bank_transfer', label: TYPE_LABELS.bank_transfer },
  { value: 'bitcoin_transfer', label: TYPE_LABELS.bitcoin_transfer },
  { value: 'litecoin_transfer', label: TYPE_LABELS.litecoin_transfer },
  { value: 'dogecoin_transfer', label: TYPE_LABELS.dogecoin_transfer },
  { value: 'pyusd_transfer', label: TYPE_LABELS.pyusd_transfer },
  { value: 'ecash_transfer', label: TYPE_LABELS.ecash_transfer },
  { value: 'cashapp_transfer', label: TYPE_LABELS.cashapp_transfer },
  { value: 'chime_transfer', label: TYPE_LABELS.chime_transfer },
];

export const WITHDRAWAL_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  ...WITHDRAWAL_RAIL_FILTER_OPTIONS,
  { value: 'game_withdrawal', label: TYPE_LABELS.game_withdrawal },
];

const TYPE_CLASS_NAMES: Record<string, string> = {
  bank_transfer: 'bg-blue-100 text-blue-800',
  bitcoin_transfer: 'bg-orange-100 text-orange-800',
  litecoin_transfer: 'bg-gray-100 text-gray-800',
  dogecoin_transfer: 'bg-yellow-100 text-yellow-800',
  pyusd_transfer: 'bg-purple-100 text-purple-800',
  ecash_transfer: 'bg-slate-100 text-slate-800',
  cashapp_transfer: 'bg-teal-100 text-teal-800',
  chime_transfer: 'bg-cyan-100 text-cyan-800',
  game_withdrawal: 'bg-emerald-100 text-emerald-800',
  hierarchy_withdrawal: 'bg-amber-100 text-amber-800',
  credit: 'bg-emerald-100 text-emerald-800',
};

function getProviderInferredTypeLabel(provider?: string | null): string | null {
  const p = String(provider || '').toLowerCase();
  if (p === 'zeroxprocessing') return '0xProcessing payout';
  if (p === 'ggusonepay') return 'GGUSOnePay payout';
  return null;
}

export function getWithdrawalTypeLabel(
  type: string | null | undefined,
  context?: Pick<WithdrawalRequest, 'provider' | 'metadata'>
): string {
  if (context && isCreditWithdrawal({ withdrawalType: type ?? null, ...context })) {
    return 'CREDIT';
  }
  if (type === 'game_withdrawal') return 'CREDIT';
  if (type && TYPE_LABELS[type]) return TYPE_LABELS[type];
  if (!type || !TYPE_LABELS[type || '']) {
    const inferred = context ? getProviderInferredTypeLabel(context.provider) : null;
    if (inferred) return inferred;
  }
  if (type) return type;
  return 'Unclassified withdrawal';
}

export function getWithdrawalTypeClassName(
  type: string | null | undefined,
  context?: Pick<WithdrawalRequest, 'provider' | 'metadata'>
): string {
  if (context && isCreditWithdrawal({ withdrawalType: type ?? null, ...context })) {
    return TYPE_CLASS_NAMES.credit;
  }
  if (type === 'game_withdrawal') return TYPE_CLASS_NAMES.game_withdrawal;
  return TYPE_CLASS_NAMES[type || ''] ?? 'bg-gray-100 text-gray-800';
}
